/**
 * /projects/:id/scoring — classement pondéré, et pourquoi (chunk 9.AJ.1).
 *
 * ## Ce que cet écran est
 *
 * Une **aide à la décision du conseiller**. Il ordonne des offres selon des poids
 * visibles et modifiables, et rend le détail du calcul pour que « pourquoi celle-ci
 * est devant » ait toujours une réponse.
 *
 * ## Ce qu'il n'est pas
 *
 * Il ne recommande pas, ne conclut pas, n'écrit rien. Le mot « meilleur » n'apparaît
 * nulle part : un classement pondéré par des poids que quelqu'un a choisis n'est pas
 * un verdict, et le présenter comme tel ferait porter à l'outil une décision qui
 * revient au conseiller.
 *
 * Il n'est pas non plus rendu au client : le rapport client de 9.AI.1 n'en parle
 * pas, tant que la façon de présenter une recommandation n'a pas été décidée.
 *
 * ## L'écran ne recalcule RIEN
 *
 * Classement, contributions, critères écartés : tout arrive décidé par le serveur.
 * Refaire une somme pondérée dans un composant React permettrait d'afficher un score
 * que le serveur ne reconnaîtrait pas — la même divergence que 9.AI.1 a écartée pour
 * le rapport.
 */

import { comparisonFieldLabel } from "@kollectivia/shared";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";
import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { comparisonContext } from "../../lib/comparisonNavigation";
import {
  useProjectScoring,
  useUpdateCriterionWeights,
  type WeightSetting,
} from "../../lib/queries/comparisonScoring";

/** Les rôles qui peuvent RÉGLER — un poids change tous les dossiers du cabinet. */
const ROLES_REGLAGE = new Set(["super_admin", "tenant_admin"]);

function BarreScore({ total }: { total: number }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100" role="presentation">
      <div
        className="bg-brand-600 h-full rounded-full transition-[width] duration-300 motion-reduce:transition-none"
        style={{ width: `${Math.max(0, Math.min(100, total))}%` }}
      />
    </div>
  );
}

export function ProjectScoringPage() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);
  const { user } = useAuth();
  const peutRegler = ROLES_REGLAGE.has(user?.role ?? "");

  const scoring = useProjectScoring(id);
  const majPoids = useUpdateCriterionWeights();

  // Brouillon local des poids : on n'enregistre qu'au geste explicite. Les poids
  // n'ont de sens que les uns par rapport aux autres, donc enregistrer à chaque
  // frappe ferait passer le cabinet par des états qu'il n'a pas voulus.
  const [brouillon, setBrouillon] = useState<Record<string, WeightSetting> | null>(null);
  const [reglagesOuverts, setReglagesOuverts] = useState(false);

  const reglages = useMemo(() => {
    const base = scoring.data?.settings ?? [];
    return base.map(
      (s) =>
        brouillon?.[s.field_key] ?? {
          field_key: s.field_key,
          weight: s.weight,
          enabled: s.enabled,
        },
    );
  }, [scoring.data, brouillon]);

  const modifie = brouillon !== null && Object.keys(brouillon).length > 0;

  const changer = useCallback((field_key: string, patch: Partial<WeightSetting>) => {
    setBrouillon((prev) => {
      const courant = prev?.[field_key];
      const base = courant ?? { field_key, weight: 1, enabled: true };
      return { ...(prev ?? {}), [field_key]: { ...base, ...patch } };
    });
  }, []);

  const enregistrer = useCallback(() => {
    if (!brouillon) return;
    majPoids.mutate(Object.values(brouillon), { onSuccess: () => setBrouillon(null) });
  }, [brouillon, majPoids]);

  const erreur = scoring.error ?? majPoids.error;
  const texteErreur =
    erreur instanceof ApiException
      ? getApiErrorMessage(erreur, lang) || t("errors.generic")
      : erreur
        ? t("errors.generic")
        : null;

  const data = scoring.data;
  // 9.AU.1 — dictionnaire partagé, rendu inchangé.
  const libelle = (fieldKey: string) => comparisonFieldLabel(fieldKey, lang);

  return (
    <div className="mx-auto w-full max-w-none">
      <header className="workspace-command-bar mb-4 py-3 print:hidden">
        <Link
          to={`/projects/${id}/comparison${comparisonContext(params)}`}
          className="focus-visible:ring-brand-500 text-sm text-slate-500 underline-offset-2 hover:text-slate-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          {t("scoring.back_to_comparison")}
        </Link>
        <h1 className="mt-1 text-balance text-xl font-semibold text-slate-900">
          {t("scoring.title")}
        </h1>
        <p className="mt-2 text-sm font-medium text-slate-700">
          {t("reception_flow.project_scope")}
        </p>
        {/* Dit en toutes lettres ce que le classement est, et ce qu'il n'est pas. */}
        <p className="mt-1 max-w-prose text-sm text-slate-600">{t("scoring.subtitle")}</p>
      </header>

      <div aria-live="polite">
        {texteErreur ? (
          <div
            role="alert"
            className="mb-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {texteErreur}
          </div>
        ) : null}

        {scoring.isLoading && !data ? (
          <p className="text-sm text-slate-500">{t("scoring.states.loading")}</p>
        ) : null}
      </div>

      {data && data.insurers.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
          <h2 className="text-base font-semibold text-slate-900">{t("scoring.empty.title")}</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
            {t("scoring.empty.subtitle")}
          </p>
        </div>
      ) : null}

      {data && data.insurers.length > 0 ? (
        <>
          {/* Aucun critère retenu : le dire, plutôt que d'afficher un podium de zéros
              qui laisserait croire que les offres se valent. */}
          {data.total_weight === 0 ? (
            <div
              role="status"
              className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            >
              <p className="font-medium">{t("scoring.warnings.nothing_scored_title")}</p>
              <p className="mt-1 max-w-prose">{t("scoring.warnings.nothing_scored_body")}</p>
            </div>
          ) : null}

          <ul className="space-y-3">
            {data.insurers.map((ins) => (
              <li
                key={ins.submission_id}
                className="rounded-xl border border-slate-200 bg-white p-4"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="flex min-w-0 items-baseline gap-2">
                    {data.total_weight > 0 && (
                      <span className="text-sm font-semibold tabular-nums text-slate-500">
                        {t("scoring.rank", { rank: ins.rank })}
                      </span>
                    )}
                    <span className="truncate font-medium text-slate-900" translate="no">
                      {ins.insurer_name}
                    </span>
                  </div>
                  {/* Un entier : une décimale de plus serait une précision que rien
                      ne fonde. */}
                  {data.total_weight > 0 && (
                    <span className="text-lg font-semibold tabular-nums text-slate-900">
                      {t("scoring.score_value", { score: ins.total })}
                    </span>
                  )}
                </div>

                {data.total_weight > 0 && (
                  <div className="mt-2">
                    <BarreScore total={ins.total} />
                  </div>
                )}

                {ins.needs_review_count > 0 || ins.has_human_overrides ? (
                  <p className="mt-2 text-xs text-amber-700">
                    {ins.needs_review_count > 0
                      ? t("scoring.flags.needs_review", { count: ins.needs_review_count })
                      : null}
                    {ins.needs_review_count > 0 && ins.has_human_overrides ? " · " : null}
                    {ins.has_human_overrides ? t("scoring.flags.human_override") : null}
                  </p>
                ) : null}

                {data.total_weight > 0 && ins.contributions.length > 0 ? (
                  <details className="mt-3">
                    <summary className="focus-visible:ring-brand-500 cursor-pointer text-sm text-slate-600 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2">
                      {t("scoring.explain.toggle")}
                    </summary>
                    <ul className="mt-2 space-y-1">
                      {ins.contributions.slice(0, 8).map((c) => (
                        <li
                          key={c.field_key}
                          className="flex items-baseline justify-between gap-3 text-sm"
                        >
                          <span className="min-w-0 truncate text-slate-700">
                            {libelle(c.field_key)}
                          </span>
                          <span className="shrink-0 tabular-nums text-slate-500">
                            {t("scoring.explain.points", { points: c.points })}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}
              </li>
            ))}
          </ul>

          {/* Ce qui n'a PAS compté. Jamais masqué : un critère écarté en silence
              laisserait croire que le classement porte sur tout le dossier. */}
          {data.excluded.length > 0 ? (
            <section className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <h2 className="text-sm font-semibold text-slate-900">
                {t("scoring.excluded.title", { count: data.excluded.length })}
              </h2>
              <p className="mt-1 max-w-prose text-sm text-slate-600">
                {t("scoring.excluded.intro")}
              </p>
              <ul className="mt-2 space-y-1">
                {data.excluded.map((ex) => (
                  <li key={ex.field_key} className="text-sm text-slate-700">
                    <span className="font-medium">{libelle(ex.field_key)}</span>
                    {" — "}
                    {t(`scoring.excluded.reason.${ex.reason}`)}
                    {ex.blocking_insurers && ex.blocking_insurers.length > 0 ? (
                      <span className="text-slate-500">
                        {" ("}
                        <span translate="no">{ex.blocking_insurers.join(", ")}</span>
                        {")"}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {/* Pondération : lisible par tout conseiller, modifiable par
              l'administration seulement. */}
          <section className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
            <button
              type="button"
              onClick={() => setReglagesOuverts((v) => !v)}
              aria-expanded={reglagesOuverts}
              className="hover:text-brand-700 focus-visible:ring-brand-500 text-sm font-semibold text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              {t("scoring.weights.title")}
            </button>
            <p className="mt-1 max-w-prose text-sm text-slate-600">
              {peutRegler ? t("scoring.weights.intro_admin") : t("scoring.weights.intro_reader")}
            </p>

            {reglagesOuverts ? (
              <>
                <ul className="mt-3 divide-y divide-slate-100">
                  {reglages.map((r) => {
                    const meta = data.settings.find((s) => s.field_key === r.field_key)!;
                    return (
                      <li
                        key={r.field_key}
                        className="flex flex-wrap items-center justify-between gap-3 py-2"
                      >
                        <div className="min-w-0">
                          <span className="text-sm text-slate-800">{libelle(r.field_key)}</span>
                          <span className="ml-2 text-xs text-slate-500">
                            {t(`scoring.direction.${meta.direction}`)}
                            {" · "}
                            {t(`scoring.source.${meta.source}`)}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <label className="flex items-center gap-1.5 text-xs text-slate-600">
                            <input
                              type="checkbox"
                              checked={r.enabled}
                              disabled={!peutRegler}
                              onChange={(e) => changer(r.field_key, { enabled: e.target.checked })}
                              className="text-brand-600 focus-visible:ring-brand-500 h-4 w-4 rounded border-slate-300 focus-visible:outline-none focus-visible:ring-2"
                            />
                            {t("scoring.weights.enabled")}
                          </label>
                          <label className="flex items-center gap-1.5 text-xs text-slate-600">
                            {t("scoring.weights.weight")}
                            <input
                              type="number"
                              min={0}
                              max={1000}
                              step={0.5}
                              inputMode="decimal"
                              value={r.weight}
                              disabled={!peutRegler}
                              onChange={(e) =>
                                changer(r.field_key, { weight: Number(e.target.value) })
                              }
                              className="focus-visible:ring-brand-500 w-20 rounded-md border border-slate-200 px-2 py-1 text-right tabular-nums focus-visible:outline-none focus-visible:ring-2"
                            />
                          </label>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                {peutRegler ? (
                  <div className="mt-3 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={enregistrer}
                      disabled={!modifie || majPoids.isPending}
                      className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 touch-manipulation rounded-lg px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-60"
                    >
                      {majPoids.isPending ? t("scoring.weights.saving") : t("scoring.weights.save")}
                    </button>
                    {modifie ? (
                      <button
                        type="button"
                        onClick={() => setBrouillon(null)}
                        className="focus-visible:ring-brand-500 text-sm text-slate-600 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                      >
                        {t("common.cancel")}
                      </button>
                    ) : null}
                    {/* Le changement ne vaut pas que pour ce dossier. */}
                    <span className="text-xs text-slate-500">
                      {t("scoring.weights.scope_hint")}
                    </span>
                  </div>
                ) : null}
              </>
            ) : null}
          </section>
        </>
      ) : null}
    </div>
  );
}

export default ProjectScoringPage;
